import React from 'react';

const EFFECTIVE_DATE = 'June 4, 2026';
const CONTACT_EMAIL = 'info@interiorfolio.com';
const COMPANY_NAME = 'InteriorFolio';
const SITE_URL = 'interiorfolio.com';

const styles = {
  page: {
    padding: '60px 24px',
    maxWidth: 820,
    margin: '0 auto',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: '#1a1a2e',
    lineHeight: 1.75,
  },
  header: {
    borderBottom: '2px solid #e8e4df',
    paddingBottom: 24,
    marginBottom: 40,
  },
  title: {
    fontSize: 36,
    fontWeight: 700,
    margin: '0 0 8px',
    color: '#1a1a2e',
  },
  subtitle: {
    fontSize: 14,
    color: '#6b7280',
    margin: 0,
  },
  section: {
    marginBottom: 40,
  },
  h2: {
    fontSize: 20,
    fontWeight: 600,
    color: '#1a1a2e',
    marginBottom: 12,
    marginTop: 0,
    paddingBottom: 6,
    borderBottom: '1px solid #f3f0ec',
  },
  p: {
    margin: '0 0 14px',
    fontSize: 15,
    color: '#374151',
  },
  pAllCaps: {
    margin: '0 0 14px',
    fontSize: 14,
    color: '#374151',
    fontWeight: 600,
    lineHeight: 1.7,
  },
  ul: {
    paddingLeft: 22,
    margin: '0 0 14px',
    fontSize: 15,
    color: '#374151',
  },
  li: {
    marginBottom: 6,
  },
  link: {
    color: '#8b6f5e',
    textDecoration: 'none',
  },
  infoBox: {
    background: '#faf8f6',
    border: '1px solid #e8e4df',
    borderRadius: 8,
    padding: '16px 20px',
    marginBottom: 14,
    fontSize: 14,
    color: '#4b5563',
  },
};

export default function TermsOfService() {
  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <h1 style={styles.title}>Terms of Service</h1>
        <p style={styles.subtitle}>Last updated: {EFFECTIVE_DATE}</p>
      </header>

      {/* Agreement */}
      <div style={styles.section}>
        <h2 style={styles.h2}>Agreement to Terms</h2>
        <p style={styles.p}>
          These Terms of Service constitute a legally binding agreement between you, whether
          personally or on behalf of an entity ("you"), and {COMPANY_NAME} ("we," "us," or "our"),
          concerning your access to and use of the {SITE_URL} website and our practice-management
          platform for interior design firms (collectively, the "Service").
        </p>
        <p style={styles.p}>
          By accessing or using the Service, you confirm that you have read, understood, and agree to
          be bound by these Terms. If you do not agree, you must discontinue use immediately.
        </p>
        <p style={styles.p}>
          The Service is intended for users who are at least 18 years old and are using it for
          legitimate business purposes. Persons under 18 are not permitted to register or use the
          Service.
        </p>
      </div>

      {/* Intellectual Property */}
      <div style={styles.section}>
        <h2 style={styles.h2}>Intellectual Property Rights</h2>
        <p style={styles.p}>
          Unless otherwise indicated, the Service and all source code, databases, functionality,
          software, designs, audio, video, text, photographs, and graphics (collectively, "Content"),
          and the trademarks, service marks, and logos contained therein ("Marks") are owned or
          controlled by us and are protected by copyright, trademark, and other applicable laws.
        </p>
        <p style={styles.p}>
          The Content is provided "AS IS" for your use in connection with the Service only. You may
          not copy, reproduce, republish, upload, post, transmit, distribute, or exploit any Content
          or Marks for commercial purposes without our prior written permission.
        </p>
        <p style={styles.p}>
          You retain full ownership of all business data, files, and documents you create or upload
          within the Service (your "User Content"). By uploading User Content, you grant us a
          limited license to store and process it solely to deliver the Service to you.
        </p>
      </div>

      {/* User Representations */}
      <div style={styles.section}>
        <h2 style={styles.h2}>User Representations</h2>
        <p style={styles.p}>By using the Service, you represent and warrant that:</p>
        <ul style={styles.ul}>
          <li style={styles.li}>All registration information you provide is true, accurate, current, and complete.</li>
          <li style={styles.li}>You will maintain the accuracy of your registration information and update it as needed.</li>
          <li style={styles.li}>You have the legal capacity to enter into these Terms.</li>
          <li style={styles.li}>You are not a minor in the jurisdiction in which you reside.</li>
          <li style={styles.li}>You will not access the Service through automated or non-human means (bots, scripts, etc.).</li>
          <li style={styles.li}>You will not use the Service for any illegal or unauthorized purpose.</li>
          <li style={styles.li}>Your use of the Service will not violate any applicable law or regulation.</li>
        </ul>
      </div>

      {/* User Registration */}
      <div style={styles.section}>
        <h2 style={styles.h2}>User Registration</h2>
        <p style={styles.p}>
          You are required to register an account to use the Service. You agree to keep your
          password confidential and are responsible for all activity that occurs under your account.
          We reserve the right to remove, reclaim, or change a username that is inappropriate,
          obscene, or otherwise objectionable at our sole discretion.
        </p>
      </div>

      {/* Fees and Payment */}
      <div style={styles.section}>
        <h2 style={styles.h2}>Fees and Payment</h2>
        <p style={styles.p}>
          Certain parts of the Service require a paid subscription. You agree to provide accurate
          and complete billing information and to keep it current (including payment method and
          expiration date). All payments are processed through Stripe and are in U.S. dollars.
        </p>
        <p style={styles.p}>
          By subscribing, you authorize us to charge your chosen payment method on a recurring basis
          without requiring prior approval for each charge, until you cancel. Except as expressly
          stated, all payments are non-refundable. We reserve the right to change pricing at any
          time with notice.
        </p>
      </div>

      {/* Free Trial */}
      <div style={styles.section}>
        <h2 style={styles.h2}>Free Trial</h2>
        <p style={styles.p}>
          We may offer a free trial period to new users. Your account will not be charged during the
          trial. At the end of the trial, your subscription will be suspended unless you upgrade to
          a paid plan.
        </p>
      </div>

      {/* Cancellation */}
      <div style={styles.section}>
        <h2 style={styles.h2}>Cancellation</h2>
        <p style={styles.p}>
          You may cancel your subscription at any time by contacting us at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} style={styles.link}>{CONTACT_EMAIL}</a>. Your
          cancellation takes effect at the end of the current paid billing period. If you are
          unsatisfied with the Service, please reach out so we can help.
        </p>
      </div>

      {/* Prohibited Activities */}
      <div style={styles.section}>
        <h2 style={styles.h2}>Prohibited Activities</h2>
        <p style={styles.p}>
          You may not access or use the Service for any purpose other than its intended use. As a
          user, you agree not to:
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}>Systematically retrieve data to build a competing database or directory without our written permission.</li>
          <li style={styles.li}>Trick, defraud, or mislead us or other users, especially to learn sensitive account information.</li>
          <li style={styles.li}>Circumvent, disable, or interfere with security-related features of the Service.</li>
          <li style={styles.li}>Use any information from the Service to harass, abuse, or harm another person.</li>
          <li style={styles.li}>Make improper use of our support services or submit false abuse reports.</li>
          <li style={styles.li}>Use the Service in a manner inconsistent with any applicable laws or regulations.</li>
          <li style={styles.li}>Upload or transmit viruses, Trojan horses, or other harmful material.</li>
          <li style={styles.li}>Use automated tools such as bots, scrapers, or scripts to interact with the Service.</li>
          <li style={styles.li}>Delete or alter any copyright or proprietary rights notices from any Content.</li>
          <li style={styles.li}>Impersonate another user, person, or {COMPANY_NAME} employee or representative.</li>
          <li style={styles.li}>Interfere with or create an undue burden on the Service or its infrastructure.</li>
          <li style={styles.li}>Attempt to bypass any access controls or security measures.</li>
          <li style={styles.li}>Decompile, reverse engineer, or disassemble any software comprising the Service, except as permitted by law.</li>
          <li style={styles.li}>Use the Service to compete with us or to build a substantially similar product.</li>
          <li style={styles.li}>Sell or transfer your account or profile to another party.</li>
        </ul>
      </div>

      {/* Submissions */}
      <div style={styles.section}>
        <h2 style={styles.h2}>Feedback and Submissions</h2>
        <p style={styles.p}>
          Any questions, comments, suggestions, or feedback you provide to us ("Submissions") are
          non-confidential and may be used by us for any lawful purpose without compensation to you.
          You retain no proprietary rights in Submissions and waive any moral rights related to them.
        </p>
      </div>

      {/* Third-Party */}
      <div style={styles.section}>
        <h2 style={styles.h2}>Third-Party Services</h2>
        <p style={styles.p}>
          The Service integrates with third-party providers including Stripe (payments), Amazon Web
          Services (infrastructure), and Intuit QuickBooks (accounting sync). Links to or use of
          third-party services are subject to those parties' own terms and privacy policies. We are
          not responsible for the content, accuracy, or practices of any third-party service.
        </p>
      </div>

      {/* Service Management */}
      <div style={styles.section}>
        <h2 style={styles.h2}>Service Management</h2>
        <p style={styles.p}>
          We reserve the right to monitor the Service for violations of these Terms, take
          appropriate legal action against violators, and remove or disable content that is
          excessive, burdensome, or in violation of these Terms — all without notice or liability.
        </p>
      </div>

      {/* Privacy */}
      <div style={styles.section}>
        <h2 style={styles.h2}>Privacy Policy</h2>
        <p style={styles.p}>
          Please review our{' '}
          <a href="/privacy" style={styles.link}>Privacy Policy</a>, which explains how we collect,
          use, and protect your personal information. By using the Service, you agree to be bound by
          our Privacy Policy.
        </p>
      </div>

      {/* User Data */}
      <div style={styles.section}>
        <h2 style={styles.h2}>Your Data</h2>
        <p style={styles.p}>
          You are solely responsible for all data you transmit to or through the Service. We
          maintain reasonable backups but are not liable for any loss or corruption of your data.
          You waive any right of action against us arising from loss or corruption of such data.
        </p>
      </div>

      {/* Modifications */}
      <div style={styles.section}>
        <h2 style={styles.h2}>Modifications and Interruptions</h2>
        <p style={styles.p}>
          We reserve the right to modify, suspend, or discontinue the Service at any time and for
          any reason without notice. We may also update these Terms at any time; the "Last updated"
          date at the top of this page will reflect such changes. Your continued use of the Service
          after changes are posted constitutes your acceptance of the revised Terms.
        </p>
        <p style={styles.p}>
          We cannot guarantee the Service will be available at all times due to maintenance,
          technical issues, or factors outside our control. We are not liable for any loss or
          inconvenience caused by Service unavailability.
        </p>
      </div>

      {/* Term and Termination */}
      <div style={styles.section}>
        <h2 style={styles.h2}>Term and Termination</h2>
        <p style={styles.p}>
          These Terms remain in effect for as long as you use the Service. We reserve the right to
          deny access to, suspend, or terminate your account at any time, with or without cause,
          without notice or liability.
        </p>
        <p style={styles.p}>
          If your account is terminated, you are prohibited from re-registering under any name
          without our express written consent. We may also pursue appropriate legal action,
          including civil and criminal remedies.
        </p>
      </div>

      {/* Governing Law */}
      <div style={styles.section}>
        <h2 style={styles.h2}>Governing Law</h2>
        <p style={styles.p}>
          These Terms are governed by and construed in accordance with applicable law. Any dispute
          arising out of or relating to these Terms or your use of the Service shall be resolved
          through binding arbitration on an individual basis. Class-action arbitration and class-action
          litigation are not permitted.
        </p>
        <p style={styles.p}>
          Any cause of action arising out of or relating to these Terms must be commenced within one
          (1) year after the cause of action accrues, or it is permanently barred.
        </p>
      </div>

      {/* Disclaimer */}
      <div style={styles.section}>
        <h2 style={styles.h2}>Disclaimer of Warranties</h2>
        <p style={styles.pAllCaps}>
          THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY
          KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY,
          FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE MAKE NO WARRANTY THAT THE
          SERVICE WILL BE ERROR-FREE, SECURE, OR CONTINUOUSLY AVAILABLE.
        </p>
      </div>

      {/* Limitation of Liability */}
      <div style={styles.section}>
        <h2 style={styles.h2}>Limitation of Liability</h2>
        <p style={styles.pAllCaps}>
          TO THE FULLEST EXTENT PERMITTED BY LAW, {COMPANY_NAME.toUpperCase()} AND ITS DIRECTORS,
          EMPLOYEES, AND AGENTS WILL NOT BE LIABLE FOR ANY INDIRECT, CONSEQUENTIAL, EXEMPLARY,
          INCIDENTAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS OR DATA, ARISING FROM YOUR USE OF
          THE SERVICE. OUR TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING UNDER THESE TERMS WILL NOT
          EXCEED THE AMOUNT PAID BY YOU TO US IN THE SIX (6) MONTHS PRECEDING THE CLAIM.
        </p>
      </div>

      {/* Indemnification */}
      <div style={styles.section}>
        <h2 style={styles.h2}>Indemnification</h2>
        <p style={styles.p}>
          You agree to defend, indemnify, and hold harmless {COMPANY_NAME} and its officers,
          employees, agents, and partners from any claims, damages, losses, or expenses (including
          reasonable attorneys' fees) arising from: (1) your use of the Service; (2) your breach of
          these Terms; (3) your violation of any third-party rights; or (4) any harmful or unlawful
          act you take in connection with the Service.
        </p>
      </div>

      {/* Electronic Communications */}
      <div style={styles.section}>
        <h2 style={styles.h2}>Electronic Communications</h2>
        <p style={styles.p}>
          By using the Service, you consent to receive electronic communications from us, including
          emails and in-app notifications. You agree that electronic agreements, notices, and
          disclosures satisfy any legal requirement that such communications be in writing.
        </p>
      </div>

      {/* Contact */}
      <div style={styles.section}>
        <h2 style={styles.h2}>Contact Us</h2>
        <p style={styles.p}>
          Questions, concerns, or complaints about these Terms can be directed to:
        </p>
        <div style={styles.infoBox}>
          <strong>{COMPANY_NAME}</strong><br />
          Email:{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} style={styles.link}>{CONTACT_EMAIL}</a>
        </div>
      </div>
    </div>
  );
}
